import { Keys, StorageService } from "@app2/shared/services/storage.service";
import { TimeService } from "@app2/shared/services/time.service";
import { BeginEndRange } from "@app2/shared/type-defs";
import { DRAFT_SLUG, INCIDENT_SLUG, SlugName, TASK_SLUG, TicketState } from "@app2/ticket/ticket-constants";
import { TicketSearchParams, TicketStateParams } from "@app2/ticket/ticket-list-page/type-defs";
import {
    TicketPageFilters,
    TicketPageRequest,
    TicketSortField,
    TicketSummary,
} from "@app2/type-defs/ticket/ticket-types";

export const DEFAULT_PAGE_SIZE = 20;

export function searchParamsToRestParams(timeService: TimeService,
                                         searchParams: TicketSearchParams,
                                         page?: TicketSummary,
                                         parentSlugId?: number,
                                         includedElements?: string[]): Partial<TicketPageRequest> {
    return {
        pageSize: DEFAULT_PAGE_SIZE,
        sort: <TicketSortField>searchParams.sort.field,
        descending: searchParams.sort.desc,
        pageStart: page,
        filters: makePageFilters(timeService, searchParams, parentSlugId),
        pageNumber: searchParams.pageNumber,
        paginationRange: 10,
        numLogs: 0,
        includedElements: includedElements,
    };
}

export function makePageFilters(timeService: TimeService,
                                searchParams: Partial<TicketSearchParams>,
                                parentSlugId?: number): Partial<TicketPageFilters> {
    const filters: Partial<TicketPageFilters> = {};
    if (searchParams.ticketState &&
        !searchParams.ticketState.includes("ALL") &&
        searchParams.ticketState.length > 0) {
        filters.state = searchParams.ticketState.flatMap(state => {
            if (state === "ALL_OPEN_INCIDENTS") {
                return ["Triage", "Analysis", "Open"];
            } else if (state === "NEW_AND_OPEN") {
                return ["Open", "New"];
            } else {
                return [state];
            }
        });
    }
    if (searchParams.ticketOverdue && searchParams.ticketOverdue === "OVERDUE") {
        filters.dueBefore = timeService.now().startOf("day").toISOString();
    } else if (searchParams.ticketOverdue && searchParams.ticketOverdue === "HAS_DUE_DATE") {
        filters.hasDueDate = true;
    } else if (searchParams.ticketOverdue && searchParams.ticketOverdue === "DUE_TODAY") {
        filters.dueAfter = timeService.now().startOf("day").toISOString();
        filters.dueBefore = timeService.now().endOf("day").toISOString();
    } else if (searchParams.ticketOverdue && searchParams.ticketOverdue === "DUE_LATER") {
        filters.dueAfter = timeService.now().endOf("day").toISOString();
    } else if (searchParams.ticketOverdue && searchParams.ticketOverdue === "NO_DUE_DATE") {
        filters.hasDueDate = false;
    }
    if (searchParams.query) {
        filters.search = searchParams.query.trim();
    }
    if (searchParams.owner) {
        filters.owner = searchParams.owner === "all" ? undefined : searchParams.owner;
    }
    if (searchParams.owners) {
        filters.owners = searchParams.owners.has("all") ? undefined : Array.from(searchParams.owners);
    }
    if (searchParams.createdBy) {
        filters.creator = searchParams.createdBy === "all" ? undefined : searchParams.createdBy;
    }
    if (searchParams.creators) {
        filters.creators = searchParams.creators.has("all") ? undefined : Array.from(searchParams.creators);
    }
    if (searchParams.createdOn?.range && searchParams.createdOn.intervalType) {
        filters.after = searchParams.createdOn.range.begin;
        filters.before = searchParams.createdOn.range.end;
    }
    if (parentSlugId) {
        filters.parentSlugId = parentSlugId;
    }
    if (searchParams.taskScheduleId) {
        filters.taskScheduleId = searchParams.taskScheduleId;
    }
    if (searchParams.triggerId) {
        filters.triggerId = searchParams.triggerId;
    }
    if (searchParams.severity && searchParams.severity !== "ALL" ) {
        filters.severity = searchParams.severity;
    }
    if (searchParams.severities && !searchParams.severities.has("ALL") ) {
        filters.severities = Array.from(searchParams.severities);
    }
    if (searchParams.lineItemId) {
        filters.lineItemIds = [searchParams.lineItemId];
    }
    if (searchParams.riskControlId) {
        filters.riskControlIds = [searchParams.riskControlId];
    }
    if (searchParams.org) {
        filters.org = searchParams.org === "all" ? undefined : searchParams.org;
    }
    if (searchParams.tagIds && searchParams.tagIds.length > 0) {
        filters.tagIds = searchParams.tagIds;
    }
    if (searchParams.isTracContactOwned) {
        filters.isTracContactOwned = searchParams.isTracContactOwned;
    }
    return filters;
}

function getInitialSearchParams(slugName: SlugName): TicketSearchParams {
    return {
        sort: {
            field: "UPDATED_ON",
            desc: true,
        },
        pageNumber: 1,
        query: "",
        view: undefined,
        ticketState: defaultTicketStatesForSlug(slugName),
        owner: "all",
        owners: undefined,
        createdBy: "all",
        creators: undefined,
        createdOn: {
            range: undefined,
            intervalType: undefined,
        },
        ticketOverdue: "ALL",
        severity: "ALL",
        severities: undefined,
        lineItemId: undefined,
        riskControlId: undefined,
        org: "all",
        tagIds: [],
        isTracContactOwned: undefined,
    };
}

export function stateToSearchParams(slugName: SlugName, stateParams: TicketStateParams, storageService: StorageService): TicketSearchParams {
    const initialSearchParams = getInitialSearchParams(slugName);
    // should this be a less strict check? i.e. null goes through then sets the order to false
    const orderDesc: boolean = stateParams.orderDesc === undefined
        ? initialSearchParams.sort.desc
        : stateParams.orderDesc === "true";
    const localStorageValue = storageService.get(Keys.taskView) ? storageService.get(Keys.taskView) : "default";
    const taskView = slugName === TASK_SLUG && !stateParams.view ? localStorageValue : ( stateParams.view || initialSearchParams.view);

    let createdOnRange: BeginEndRange = undefined;
    if (stateParams.createdAfter && stateParams.createdBefore) {
        createdOnRange = {
            begin: stateParams.createdAfter,
            end: stateParams.createdBefore,
        };
    }

    return {
        pageNumber: +stateParams.page || initialSearchParams.pageNumber,
        sort: {
            field: stateParams.orderField || initialSearchParams.sort.field,
            desc: orderDesc,
        },
        query: stateParams.searchTerm || "",
        view: slugName === TASK_SLUG ? taskView : "default",
        ticketState: stateParams.ticketState ? coercePossibleSingleValueToArray(stateParams.ticketState) : initialSearchParams.ticketState,
        owner: stateParams.owner || initialSearchParams.owner,
        owners: stateParams.owners ? new Set(stateParams.owners.split(",")) : initialSearchParams.owners,
        createdBy: stateParams.createdBy || initialSearchParams.createdBy,
        creators: stateParams.creators ? new Set(stateParams.creators.split(",")) : initialSearchParams.creators,
        createdOn: {
            range: createdOnRange,
            intervalType: stateParams.createdIntervalType,
        },
        ticketOverdue: stateParams.ticketOverdue ? stateParams.ticketOverdue : initialSearchParams.ticketOverdue,
        taskScheduleId: stateParams.taskScheduleId,
        triggerId: stateParams.triggerId,
        severity: stateParams.severity,
        severities: stateParams.severities ? new Set(stateParams.severities.split(",")) : initialSearchParams.severities,
        lineItemId: stateParams.lineItemId,
        riskControlId: stateParams.riskControlId,
        isTracContactOwned: stateParams.isTracContactOwned === "true",
        org: stateParams.org,
        // route.queryParams assumes tagIds should be a string when refreshing the page with one tag selected
        tagIds: stateParams.tagIds ? coercePossibleSingleValueToArray(stateParams.tagIds) : initialSearchParams.tagIds
    };
}

function coercePossibleSingleValueToArray(value: unknown) {
    if (Array.isArray(value)) return value;
    return [value];
}

export function defaultTicketStatesForSlug(slugName: SlugName): TicketState[] {
    switch (slugName) {
        case INCIDENT_SLUG:
            return ["ALL_OPEN_INCIDENTS"];
        case DRAFT_SLUG:
            return ["ALL_OPEN_INCIDENTS", "New"];
        case TASK_SLUG:
            return ["NEW_AND_OPEN", "In_Progress", "Pending"];
        default:
            return ["ALL_OPEN_INCIDENTS"];
    }
}

export function stateParamsDefaults() {
    return {
        page: null,
        orderDesc: null,
        searchTerm: null,
        orderField: null,
        policyId: null,
        lineItemId: null,
        view: null,
        ticketState: null,
        owner: null,
        owners: null,
        createdBy: null,
        creators: null,
        createdAfter: null,
        createdBefore: null,
        createdIntervalType: null,
        ticketOverdue: null,
        severity: null,
        severities: null,
        tagIds: null,
        isTracContactOwned: null,
    };
}
